import { useState, type ReactNode } from 'react';
import { externalHref } from '../lib/url';

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** Highlight every occurrence of the brand tokens in the answer text. */
function highlight(text: string, tokens: string[]): ReactNode[] {
  const clean = tokens.map((t) => t.trim()).filter((t) => t.length > 1);
  if (clean.length === 0) return [text];
  const re = new RegExp(`(${clean.map(escapeRegex).join('|')})`, 'gi');
  return text.split(re).map((part, i) =>
    clean.some((t) => t.toLowerCase() === part.toLowerCase()) ? (
      <mark key={i} className="bg-cyan/25 text-cyan rounded px-0.5">
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

/**
 * Shows the actual AI answer with the brand highlighted where mentioned, and the
 * cited sources (brand domain highlighted if present). Collapsible to keep the
 * list scannable.
 */
export default function AnswerBlock({
  answer,
  citations,
  brandTokens,
  brandDomain,
}: {
  answer: string;
  citations: string[];
  brandTokens: string[];
  brandDomain: string;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (!answer && citations.length === 0) return null;

  return (
    <div className="mt-2">
      <button onClick={() => setOpen((o) => !o)} className="font-mono text-sm text-cyan hover:underline">
        {open ? 'hide AI answer ↑' : 'show AI answer ↓'}
      </button>
      {open && (
        <div className="mt-2 rounded-lg bg-warm-dark-2/40 border border-warm-dark-2 p-3">
          <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap">
            {highlight(answer, brandTokens)}
          </p>
          {citations.length > 0 && (
            <div className="mt-3 border-t border-warm-dark-2 pt-2">
              <p className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-1">Cited sources</p>
              <div className="flex flex-col gap-1">
                {citations.map((c) => {
                  const isOurs = hostOf(c).toLowerCase().endsWith(brandDomain.toLowerCase());
                  return (
                    <a
                      key={c}
                      href={externalHref(c)}
                      target="_blank"
                      rel="noreferrer"
                      className={`text-sm truncate hover:underline ${isOurs ? 'text-cyan font-medium' : 'text-text-secondary'}`}
                    >
                      {isOurs ? '★ ' : ''}
                      {hostOf(c)}
                      {isOurs ? ' (you)' : ''}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
